import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { FaRegCommentDots } from 'react-icons/fa';
import { sendQuoteEmail } from '../lib/emailService';
import './contact.css';

function GetQuote() {
  const serviceOptions = useMemo(
    () => [
      'Water supply system installation',
      'Pump installation / maintenance / repair',
      'Pipeline installation / rehabilitation',
      'Water meter installation / calibration',
      'Fabrication / welding works',
      'Solar-powered water systems',
      'Other',
    ],
    []
  );

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    serviceType: '',
    budget: '',
    timeline: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState('');

  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const [serviceTypeError, setServiceTypeError] = useState('');
  const serviceDropdownRef = useRef(null);
  const serviceTriggerRef = useRef(null);

  useEffect(() => {
    if (!serviceMenuOpen) return;

    const handlePointerDown = (event) => {
      const root = serviceDropdownRef.current;
      if (!root) return;
      if (root.contains(event.target)) return;
      setServiceMenuOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setServiceMenuOpen(false);
      serviceTriggerRef.current?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [serviceMenuOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectServiceType = (value) => {
    setFormData((prev) => ({ ...prev, serviceType: value }));
    setServiceTypeError('');
    setServiceMenuOpen(false);
    serviceTriggerRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.serviceType) {
      setServiceTypeError('Please select a service type.');
      serviceTriggerRef.current?.focus();
      return;
    }

    setFormStatus('sending');

    try {
      const response = await sendQuoteEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        serviceType: formData.serviceType,
        budget: formData.budget,
        timeline: formData.timeline,
        message: formData.message,
      });
      console.log(response.data);
      setFormStatus('success');
      setTimeout(() => {
        setFormStatus('');
        setServiceTypeError('');
        setServiceMenuOpen(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          serviceType: '',
          budget: '',
          timeline: '',
          message: '',
        });
      }, 3000);
    } catch (error) {
      console.error('There was an error sending the quote request!', error);
      setFormStatus('error');
      setTimeout(() => {
        setFormStatus('');
      }, 3000);
    }
  };

  return (
    <div>
      <section className="contact-page">
        <div className="contact-wrap">
          <div className="contact-hero">
            <div className="contact-hero-left">
              <div className="contact-pill">
                <span>Get a Quote</span>
              </div>
              <h1 className="contact-title">Request a Quote</h1>
              <p className="contact-subtitle">
                Tell us what you need and we’ll respond with pricing, timelines, and next steps.
              </p>
              <a href="tel:0881816790" className="contact-cta">
                <span>Call Now</span>
              </a>
            </div>

            <div className="contact-hero-right">
              <img
                className="contact-hero-image"
                src="/images/44.jpg"
                alt="Request a quote from Divine-Tech Engineering"
                loading="lazy"
              />
            </div>
          </div>

          <div className="contact-content">
            <div className="contact-grid">
              <div className="quote-left-column">
                <aside className="contact-card quote-info-card">
                  <div className="contact-card-icon" aria-hidden="true">
                    <span>✓</span>
                  </div>
                  <h3 className="contact-card-title">What to Include</h3>
                  <p className="contact-card-subtitle">
                    To help us quote accurately, include as many details as you can.
                  </p>

                  <ul className="quote-checklist">
                    <li>Exact location (district/town) and site access notes</li>
                    <li>Type of work (installation, maintenance, fabrication)</li>
                    <li>Quantities/measurements (pipe length, tank size, meters)</li>
                    <li>Timeline or deadline (if any)</li>
                    <li>Any photos or drawings (you can share later via email)</li>
                  </ul>
                </aside>

                <div className="quote-info-media" aria-hidden="true">
                  <img
                    className="quote-info-image"
                    src="/images/20.jpg"
                    alt=""
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="contact-card contact-form-card">
                <div className="contact-card-icon" aria-hidden="true">
                  <FaRegCommentDots />
                </div>
                <h3 className="contact-card-title">Quote Details</h3>
                <p className="contact-card-subtitle">
                  Share your requirements and we’ll get back to you.
                </p>

                <Form onSubmit={handleSubmit} className="contact-form">
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="quoteName" className="mb-3">
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="contact-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="quoteEmail" className="mb-3">
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder="Email Address"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="contact-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="quotePhone" className="mb-3">
                        <Form.Control
                          type="tel"
                          name="phone"
                          placeholder="Phone Number (optional)"
                          value={formData.phone}
                          onChange={handleChange}
                          className="contact-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="quoteLocation" className="mb-3">
                        <Form.Control
                          type="text"
                          name="location"
                          placeholder="Location (district/town)"
                          value={formData.location}
                          onChange={handleChange}
                          required
                          className="contact-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="quoteServiceType" className="mb-3">
                        <div className="quote-select" ref={serviceDropdownRef}>
                          <button
                            ref={serviceTriggerRef}
                            type="button"
                            className={`contact-input quote-select-trigger${serviceTypeError ? ' quote-select-trigger--error' : ''}`}
                            aria-haspopup="listbox"
                            aria-expanded={serviceMenuOpen}
                            onClick={() => {
                              setServiceTypeError('');
                              setServiceMenuOpen((open) => !open);
                            }}
                          >
                            <span className={formData.serviceType ? 'quote-select-value' : 'quote-select-placeholder'}>
                              {formData.serviceType || 'Select Service Type'}
                            </span>
                            <span className="quote-select-chevron" aria-hidden="true">▾</span>
                          </button>

                          {serviceMenuOpen && (
                            <div className="quote-select-menu" role="listbox">
                              {serviceOptions.map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  role="option"
                                  aria-selected={formData.serviceType === option}
                                  className={`quote-select-option${formData.serviceType === option ? ' quote-select-option--selected' : ''}`}
                                  onClick={() => handleSelectServiceType(option)}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          )}

                          {serviceTypeError && (
                            <div className="quote-field-error" role="alert">
                              {serviceTypeError}
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="quoteTimeline" className="mb-3">
                        <Form.Control
                          type="text"
                          name="timeline"
                          placeholder="Timeline / deadline (optional)"
                          value={formData.timeline}
                          onChange={handleChange}
                          className="contact-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group controlId="quoteBudget" className="mb-3">
                    <Form.Control
                      type="text"
                      name="budget"
                      placeholder="Budget range (optional)"
                      value={formData.budget}
                      onChange={handleChange}
                      className="contact-input"
                    />
                  </Form.Group>

                  <Form.Group controlId="quoteMessage" className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={6}
                      name="message"
                      placeholder="What are you looking to build or install? Include any details that help us estimate accurately."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="contact-input"
                    />
                  </Form.Group>

                  <Button type="submit" className="contact-submit" disabled={formStatus === 'sending'}>
                    {formStatus === 'sending' ? 'Sending…' : 'Request Quote'}
                  </Button>

                  {formStatus === 'success' && (
                    <div className="contact-form-status contact-form-status--success" role="status">
                      Quote request sent successfully.
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="contact-form-status contact-form-status--error" role="status">
                      Failed to send quote request. Please try again.
                    </div>
                  )}
                </Form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default GetQuote;
